import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, Plus, Trash2, Check, X,
  ChevronDown, ChevronRight, ArrowLeft,
  Swords, RefreshCw, BookOpen, AlertCircle, Layers, Sparkles
} from 'lucide-react';
import { useAuth } from 'contexts/AuthContext';

/* ─── GLOBAL STYLES (Cyberpunk Aesthetic) ────────────────────────────────── */
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
  
  @keyframes scanLine { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

  .dash-root { 
    font-family: 'DM Sans', sans-serif; 
    min-height: 100vh;
    background-color: #060810;
    background-image: url('/bg-dashboard.png'); /* Asegúrate de tener este fondo en public/ */
    background-size: cover;
    background-position: center top;
    background-attachment: fixed;
    position: relative;
    overflow-x: hidden;
    animation: fadeIn 0.4s ease;
    color: #ffffff;
  }

  .dash-root::before {
    content: ''; position: fixed; top: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.4), transparent);
    animation: scanLine 8s linear infinite; z-index: 1; pointer-events: none;
  }

  .main-content {
    position: relative; z-index: 2; padding: 32px 40px; max-width: 72rem; margin: 0 auto;
  }

  .glass-panel {
    background: rgba(10, 8, 30, 0.65);
    backdrop-filter: blur(24px) saturate(160%);
    -webkit-backdrop-filter: blur(24px) saturate(160%);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 20px;
    position: relative;
    transition: all 0.3s ease;
    box-shadow: 0 4px 32px rgba(0, 0, 0, 0.4), inset 0 0 0 0.5px rgba(255,255,255,0.05);
  }

  .title-neon {
    color: #FFF;
    text-shadow: 0 0 15px rgba(0, 255, 255, 0.4);
    letter-spacing: -0.02em;
  }

  /* ── GLOW EFFECTS ── */
  .glow-cyan { box-shadow: 0 0 20px rgba(0, 255, 255, 0.08); border-color: rgba(0, 255, 255, 0.2); }
  .glow-magenta { box-shadow: 0 0 20px rgba(255, 0, 255, 0.08); border-color: rgba(255, 0, 255, 0.2); }
  .glow-lime { box-shadow: 0 0 20px rgba(204, 255, 0, 0.08); border-color: rgba(204, 255, 0, 0.2); }
  
  /* ── NEON BUTTONS ── */
  .btn-neon-cyan {
    background: #00FFFF; color: #000; font-weight: 800; border: none;
    box-shadow: 0 0 15px rgba(0, 255, 255, 0.4); transition: all 0.2s;
  }
  .btn-neon-cyan:hover:not(:disabled) { background: #66FFFF; box-shadow: 0 0 25px rgba(0, 255, 255, 0.6); transform: translateY(-1px); }
  .btn-neon-cyan:disabled { background: rgba(0, 255, 255, 0.1); color: rgba(0, 255, 255, 0.3); box-shadow: none; cursor: not-allowed; }

  .btn-neon-magenta {
    background: #FF00FF; color: #fff; font-weight: 800; border: none;
    box-shadow: 0 0 15px rgba(255, 0, 255, 0.4); transition: all 0.2s;
  }
  .btn-neon-magenta:hover:not(:disabled) { background: #ff4dff; box-shadow: 0 0 25px rgba(255, 0, 255, 0.6); transform: translateY(-1px); }
  .btn-neon-magenta:disabled { background: rgba(255, 0, 255, 0.1); color: rgba(255, 0, 255, 0.3); box-shadow: none; cursor: not-allowed; }

  .btn-neon-lime {
    background: #ccff00; color: #000; font-weight: 800; border: none;
    box-shadow: 0 0 15px rgba(204, 255, 0, 0.4); transition: all 0.2s;
  }
  .btn-neon-lime:hover:not(:disabled) { background: #d4ff33; box-shadow: 0 0 25px rgba(204, 255, 0, 0.6); transform: translateY(-1px); }
  .btn-neon-lime:disabled { background: rgba(204, 255, 0, 0.1); color: rgba(204, 255, 0, 0.3); box-shadow: none; cursor: not-allowed; }

  /* Custom Scrollbar */
  ::-webkit-scrollbar { width: 8px; }
  ::-webkit-scrollbar-track { background: rgba(255,255,255,0.02); }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(0,255,255,0.3); }
`;

const ModuleTestsManager = () => {
  const { moduleId } = useParams();
  const navigate     = useNavigate();
  const { api, user } = useAuth();

  const [module, setModule]       = useState(null);
  const [tests, setTests]         = useState([]);
  const [subModules, setSubModules] = useState([]);
  const [roleplay, setRoleplay]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [generating, setGenerating]                 = useState(false);
  const [generatingRoleplay, setGeneratingRoleplay] = useState(false);
  const [expandedSections, setExpandedSections]     = useState({});

  useEffect(() => { loadAll(); }, [moduleId]);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadModule(), loadTests(), loadRoleplay()]);
    setLoading(false);
  };

  const loadModule = async () => {
    try {
      if (user?.role === 'superadmin') {
        const res = await api.get('/superadmin/all-modules');
        const mod = res.data.find(m => m.id === moduleId) || null;
        setModule(mod);
        if (mod?.has_submodules) {
          try {
            const subsRes = await api.get(`/admin/modules/${moduleId}/submodules`);
            setSubModules(subsRes.data || []);
          } catch { setSubModules([]); }
        }
      } else {
        const res = await api.get('/admin/modules');
        const mod = res.data.find(m => m.id === moduleId) || null;
        setModule(mod);
        if (mod?.has_submodules) {
          try {
            const subsRes = await api.get(`/admin/modules/${moduleId}/submodules`);
            setSubModules(subsRes.data || []);
          } catch { setSubModules([]); }
        }
      }
    } catch (err) {
      toast.error('Error al cargar el módulo');
    }
  };

  const loadTests = async () => {
    try {
      const res = await api.get(`/admin/modules/${moduleId}/tests`);
      setTests(res.data);
    } catch {
      setTests([]);
    }
  };

  const loadRoleplay = async () => {
    try {
      const res = await api.get(`/admin/modules/${moduleId}/roleplay`);
      setRoleplay(res.data);
    } catch {
      setRoleplay(null);
    }
  };

  // ── Parser de secciones para módulos normales ──────────────────────────────
  const parseModuleSections = (content) => {
    if (!content) return [];
    const sections = [];
    const lines = content.split('\n');
    let current = null;
    let buf = [];

    lines.forEach(line => {
      const trimmed = line.trim();
      if (trimmed.startsWith('## ') || trimmed === '##') {
        if (current) {
          current.content = buf.join('\n').trim();
          sections.push(current);
        }
        const idx = sections.length + 1;
        current = {
          id: `section_${idx}`,
          title: trimmed.replaceAll(/^##\s*/g, '').trim(),
          content: ''
        };
        buf = [];
      } else if (current) {
        buf.push(line);
      }
    });
    if (current) {
      current.content = buf.join('\n').trim();
      sections.push(current);
    }
    return sections;
  };

  const getEffectiveSections = (mod) => {
    if (!mod) return [];
    const h2 = parseModuleSections(mod.content || '');
    if (h2.length > 0) return h2;
    const fullContent = mod.content || mod.description || '';
    if (fullContent.length < 100) return [];
    if (fullContent.length > 1500) {
      const thirds = Math.floor(fullContent.length / 3);
      const b1 = fullContent.slice(0, thirds).lastIndexOf('\n\n') || thirds;
      const b2 = fullContent.slice(0, thirds * 2).lastIndexOf('\n\n') || thirds * 2;
      const parts = [fullContent.slice(0, b1), fullContent.slice(b1, b2), fullContent.slice(b2)].filter(p => p.trim().length > 80);
      if (parts.length > 1) return parts.map((p, i) => ({ id: `section_${i+1}`, title: `${mod.title||'Módulo'} — Parte ${i+1}`, content: p.trim() }));
    }
    return [{ id: 'section_1', title: mod.title || 'Contenido del módulo', content: fullContent }];
  };

  // ── Regenerar test de un sub-módulo ───────────────────────────────────────
  const regenerateSubModuleTest = async (subModuleId) => {
    if (!confirm('¿Regenerar el test de esta unidad con IA?')) return;
    try {
      toast.loading('Regenerando test…', { id: `regen-${subModuleId}` });
      await api.post(`/admin/submodules/${subModuleId}/regenerate-test`);
      toast.success('Test regenerado ✓', { id: `regen-${subModuleId}` });
      await loadTests();
      const subsRes = await api.get(`/admin/modules/${moduleId}/submodules`);
      setSubModules(subsRes.data || []);
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message), { id: `regen-${subModuleId}` });
    }
  };

  // ── Generar tests para módulo normal ──────────────────────────────────────
  const generateAllTests = async () => {
    if (!module?.content && !module?.description) {
      toast.error('El módulo no tiene contenido para generar tests');
      return;
    }
    const effectiveSections = getEffectiveSections(module);
    if (effectiveSections.length === 0) {
      toast.error('No se encontró contenido suficiente en el módulo');
      return;
    }
    const sectionsWithoutTest = effectiveSections.filter(
      s => !tests.find(t => t.section_id === s.id || t.section_title?.toLowerCase() === s.title?.toLowerCase())
    );
    if (sectionsWithoutTest.length === 0) {
      toast.info('Todas las secciones ya tienen tests. Usa "Regenerar Todos" para reemplazarlos.');
      return;
    }
    setGenerating(true);
    toast.info(`Generando tests para ${sectionsWithoutTest.length} sección(es)...`);
    try {
      const res = await api.post(`/admin/modules/${moduleId}/generate-tests`, { sections: sectionsWithoutTest });
      if (res.data.tests_generated > 0) {
        toast.success(`✅ ${res.data.tests_generated} tests generados`);
        await loadTests();
      } else {
        toast.warning('No se generaron tests. Verifica el contenido.');
      }
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const regenerateAllTests = async () => {
    if (!confirm('¿Borrar todos los tests actuales y regenerarlos desde cero?')) return;
    setGenerating(true);
    for (const test of tests) {
      try { await api.delete(`/admin/modules/${moduleId}/tests/${test.id}`); } catch {}
    }
    setTests([]);
    const effectiveSections = getEffectiveSections(module);
    try {
      const res = await api.post(`/admin/modules/${moduleId}/generate-tests`, { sections: effectiveSections });
      if (res.data.tests_generated > 0) {
        toast.success(`✅ ${res.data.tests_generated} tests regenerados`);
        await loadTests();
      }
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const generateRoleplay = async () => {
    if (!module?.content && !module?.description) {
      toast.error('El módulo no tiene contenido para generar el roleplay');
      return;
    }
    setGeneratingRoleplay(true);
    toast.info('Generando escenario de roleplay con IA...');
    try {
      const res = await api.post(`/admin/modules/${moduleId}/generate-roleplay`);
      setRoleplay(res.data);
      toast.success('✅ Escenario de roleplay generado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar roleplay');
    } finally {
      setGeneratingRoleplay(false);
    }
  };

  const deleteTest = async (testId, e) => {
    e.stopPropagation();
    if (!confirm('¿Eliminar este test?')) return;
    try {
      await api.delete(`/admin/modules/${moduleId}/tests/${testId}`);
      toast.success('Test eliminado');
      await loadTests();
    } catch {
      toast.error('Error al eliminar test');
    }
  };

  const toggleSection = (id) =>
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) {
    return (
      <div className="dash-root flex items-center justify-center">
        <style>{GLOBAL_STYLES}</style>
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-[#00FFFF] drop-shadow-[0_0_10px_#00FFFF]" />
          <span className="text-[10px] font-black uppercase tracking-widest text-[#00FFFF]">Inicializando Administrador...</span>
        </div>
      </div>
    );
  }

  const hasSubModules = module?.has_submodules && subModules.length > 0;
  const sections = (!hasSubModules && module) ? getEffectiveSections(module) : [];
  const parsedH2 = (!hasSubModules && module) ? parseModuleSections(module.content || '') : [];
  const usingFallback = !hasSubModules && parsedH2.length === 0 && sections.length > 0;
  const totalQuestions = tests.reduce((a, t) => a + (t.questions?.length || 0), 0);

  // ── Para módulos con sub-unidades: agrupar tests por sub_module_id ─────────
  const testsBySubModule = {};
  if (hasSubModules) {
    tests.forEach(t => {
      const key = t.sub_module_id || 'sin_unidad';
      if (!testsBySubModule[key]) testsBySubModule[key] = [];
      testsBySubModule[key].push(t);
    });
  }

  return (
    <div className="dash-root">
      <style>{GLOBAL_STYLES}</style>
      
      <div className="main-content space-y-6">

        {/* ── Header ── */}
        <div className="glass-panel p-8 glow-cyan">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[10px] text-[#00FFFF] hover:text-white uppercase font-black tracking-widest mb-4 transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Volver al Control
              </button>
              <h1 className="text-3xl font-black title-neon tracking-tight mb-1" style={{ fontFamily: 'Roberta, serif' }}>
                {module?.title || 'Módulo'}
              </h1>
              <p className="text-white/50 text-[11px] uppercase tracking-widest font-bold">
                {hasSubModules
                  ? `Curso SCORM detectado — ${subModules.length} unidades estructurales`
                  : 'Gestión de tests y simuladores IA'}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              {/* Solo mostrar botón de generar si NO tiene sub-módulos */}
              {!hasSubModules && (
                <>
                  <button
                    onClick={generateAllTests}
                    disabled={generating || sections.length === 0}
                    className="flex items-center gap-2 px-6 py-3 btn-neon-cyan rounded-xl text-xs uppercase tracking-widest transition-all"
                  >
                    {generating
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Procesando IA...</>
                      : <><Sparkles className="w-4 h-4" /> Generar Tests</>}
                  </button>
                  {tests.length > 0 && (
                    <button onClick={regenerateAllTests} disabled={generating}
                      className="flex items-center gap-2 px-6 py-3 bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 hover:text-red-300 rounded-xl font-black text-xs uppercase tracking-widest transition-all disabled:opacity-50">
                      <RefreshCw className="w-4 h-4" /> Resetear Todos
                    </button>
                  )}
                </>
              )}

              <button onClick={generateRoleplay} disabled={generatingRoleplay}
                className="flex items-center gap-2 px-6 py-3 btn-neon-magenta rounded-xl text-xs uppercase tracking-widest transition-all">
                {generatingRoleplay
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Creando Simulación...</>
                  : roleplay
                    ? <><RefreshCw className="w-4 h-4" /> Regenerar Roleplay</>
                    : <><Swords className="w-4 h-4" /> Crear Roleplay IA</>}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {[
              { label: hasSubModules ? 'Unidades' : 'Secciones', value: hasSubModules ? subModules.length : sections.length, color: 'text-[#00FFFF]', bg: 'bg-[#00FFFF]/10 border-[#00FFFF]/30' },
              { label: 'Tests',     value: tests.length,     color: 'text-[#ccff00]', bg: 'bg-[#ccff00]/10 border-[#ccff00]/30' },
              { label: 'Preguntas', value: totalQuestions,   color: 'text-white', bg: 'bg-white/5 border-white/10' },
              { label: 'Roleplay',  value: roleplay ? 'Activo' : 'Inactivo', color: 'text-[#FF00FF]', bg: 'bg-[#FF00FF]/10 border-[#FF00FF]/30' },
            ].map(({ label, value, color, bg }) => (
              <div key={label} className={`border rounded-xl p-4 flex flex-col items-center justify-center text-center ${bg}`}>
                <div className={`text-2xl font-black ${color}`}>{value}</div>
                <div className="text-[9px] font-bold uppercase tracking-widest mt-1 opacity-60 text-white">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Roleplay Card ── */}
        {roleplay && (
          <div className="glass-panel border-[#FF00FF]/30 p-6 glow-magenta">
            <div className="flex items-center gap-3 mb-6 border-b border-[#FF00FF]/20 pb-4">
              <div className="p-2 bg-[#FF00FF]/20 rounded-lg border border-[#FF00FF]/40 text-[#FF00FF]">
                <Swords className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-black text-white tracking-tight">{roleplay.title || 'Simulador de Roleplay'}</h2>
              <span className="ml-auto text-[9px] uppercase tracking-widest font-black text-[#FF00FF] bg-[#FF00FF]/10 border border-[#FF00FF]/30 px-3 py-1 rounded-md">
                Modelo IA Activo
              </span>
            </div>
            
            <div className="space-y-4 text-sm">
              {roleplay.description && (
                <div className="bg-[#FF00FF]/5 border border-[#FF00FF]/20 rounded-xl p-4">
                  <p className="font-black text-[#FF00FF] text-[10px] uppercase tracking-widest mb-1.5">Contexto Inicial</p>
                  <p className="text-white/80 leading-relaxed">{roleplay.description}</p>
                </div>
              )}
              {roleplay.learning_objective && (
                <div className="bg-[#00FFFF]/5 border border-[#00FFFF]/20 rounded-xl p-4">
                  <p className="font-black text-[#00FFFF] text-[10px] uppercase tracking-widest mb-1.5">Directiva de Aprendizaje</p>
                  <p className="text-white/80 leading-relaxed">{roleplay.learning_objective}</p>
                </div>
              )}
              {roleplay.steps?.length > 0 && (
                <div className="pt-4">
                  <p className="font-black text-white/50 text-[10px] uppercase tracking-widest mb-3">
                    Secuencia de Interacción ({roleplay.steps.length} Fases)
                  </p>
                  <div className="space-y-3">
                    {roleplay.steps.map((step, i) => (
                      <div key={step.id || i} className="bg-black/40 rounded-xl p-4 border border-white/5">
                        <p className="text-[10px] font-black text-[#ccff00] uppercase tracking-widest mb-1.5">Fase {i + 1}</p>
                        <p className="text-[13px] text-white/90 leading-relaxed">{step.situation}</p>
                        {step.expected_behavior && (
                          <p className="text-[11px] text-[#00FFFF]/70 mt-2 italic font-medium flex items-start gap-1.5">
                            <Sparkles className="w-3 h-3 flex-shrink-0 mt-0.5" /> {step.expected_behavior}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {!roleplay && !generatingRoleplay && (
          <div className="glass-panel border-dashed border-[#FF00FF]/30 p-12 text-center flex flex-col items-center">
            <Swords className="w-12 h-12 text-[#FF00FF]/40 mb-4" />
            <p className="font-black text-white text-lg tracking-tight mb-1">Sin Simulador de Roleplay</p>
            <p className="text-sm text-white/50 mb-6 max-w-md">El sistema de IA puede generar un entorno de pruebas conversacional basado en la teoría de este módulo.</p>
            <button onClick={generateRoleplay} className="btn-neon-magenta px-8 py-3 rounded-xl text-xs uppercase tracking-widest flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Instanciar Simulador
            </button>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* VISTA A: Módulo con sub-unidades (SCORM con unidades)              */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {hasSubModules && (
          <div className="glass-panel p-6">
            <div className="flex items-center justify-between px-2 mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#ccff00]/10 border border-[#ccff00]/30 rounded-lg text-[#ccff00]">
                  <Layers className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-black tracking-tight text-white">Unidades Estructurales</h2>
                  <span className="text-[9px] uppercase tracking-widest font-bold text-[#ccff00]">
                    Evaluaciones integradas desde paquete SCORM
                  </span>
                </div>
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-white/50 bg-black/40 px-3 py-1.5 rounded-lg border border-white/10">
                {subModules.filter(sm => testsBySubModule[sm.id]?.length > 0).length}/{subModules.length} Operativas
              </span>
            </div>

            <div className="space-y-3">
              {subModules.map((sm) => {
                const unitTests = testsBySubModule[sm.id] || [];
                const hasTest = unitTests.length > 0;
                const mainTest = unitTests[0];
                const isExpanded = expandedSections[sm.id];
                const questionCount = mainTest?.questions?.length || 0;
                const source = mainTest?.source;

                return (
                  <div key={sm.id} className="bg-black/30 border border-white/5 rounded-xl overflow-hidden transition-all hover:border-white/10">
                  
                      <div className="flex items-center justify-between hover:bg-white/5 transition-colors">
  <button type="button" onClick={() => toggleSection(sm.id)} aria-expanded={!!isExpanded}
    className="flex items-center gap-4 min-w-0 flex-1 text-left p-5 cursor-pointer">
                        <div className={`w-6 h-6 flex items-center justify-center rounded bg-white/5 text-white/40 ${isExpanded ? 'bg-white/10 text-white' : ''}`}>
                          {isExpanded ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                        </div>
                        <div>
                          <span className="text-[10px] font-black text-[#00FFFF] uppercase tracking-widest block mb-0.5">
                            Unidad {sm.unit_number}
                          </span>
                          <h3 className="font-bold text-white text-[15px] truncate">{sm.title}</h3>
                        </div>
                       </button>

                      <div className="flex items-center gap-4 flex-shrink-0 py-5 pr-5">
                        {hasTest ? (
                          <div className="flex items-center gap-3">
                            <span className="text-[11px] font-black text-[#ccff00] bg-[#ccff00]/10 border border-[#ccff00]/20 px-2.5 py-1 rounded flex items-center gap-1.5 uppercase tracking-widest">
                              <Check className="w-3 h-3" /> {questionCount} Qs
                            </span>
                            {source && (
                              <span className={`text-[9px] px-2 py-1.5 rounded font-black uppercase tracking-widest border ${
                                source === 'pdf_extracted'
                                  ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                  : 'bg-orange-500/10 text-orange-400 border-orange-500/20'
                              }`}>
                                {source === 'pdf_extracted' ? 'EXTRACCIÓN PDF' : 'SÍNTESIS IA'}
                              </span>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); regenerateSubModuleTest(sm.id); }}
                              className="p-2 text-[#00FFFF]/70 hover:text-[#00FFFF] hover:bg-[#00FFFF]/10 rounded-lg transition-colors"
                              title="Forzar Regeneración IA"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5 text-white/30 text-[10px] font-black uppercase tracking-widest">
                              <X className="w-3.5 h-3.5" /> Sin Evaluación
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); regenerateSubModuleTest(sm.id); }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00FFFF]/10 text-[#00FFFF] border border-[#00FFFF]/30 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[#00FFFF]/20 transition-colors"
                            >
                              <Sparkles className="w-3 h-3" /> Generar
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {isExpanded && mainTest && (
                      <div className="border-t border-white/5 p-5 bg-black/50">
                        <p className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-4 flex items-center gap-2">
                          Banco de Preguntas ({questionCount})
                          {source === 'pdf_extracted' && <span className="text-blue-400">— Origen: SCORM Metadata</span>}
                          {source === 'ai_generated' && <span className="text-orange-400">— Origen: Inferencia IA</span>}
                        </p>
                        
                        <div className="space-y-3">
                          {mainTest.questions?.map((q, qIdx) => (
                            <div key={q.id || qIdx} className="bg-white/5 rounded-xl border border-white/5 p-5">
                              <div className="flex items-center gap-3 mb-3">
                                <span className="w-6 h-6 flex items-center justify-center bg-white/10 text-white/50 text-[10px] font-black rounded-md">Q{qIdx + 1}</span>
                                <span className="px-2.5 py-0.5 bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] text-[10px] font-black uppercase tracking-widest rounded-md">{q.points} PTS</span>
                              </div>
                              <p className="font-bold mb-4 text-[14px] text-white leading-snug">{q.question}</p>
                              
                              <div className="space-y-2 mb-4">
                                {q.options?.map(opt => (
                                  <div key={opt.id} className={`p-3 rounded-lg text-[13px] flex items-center gap-3 border ${
                                    opt.is_correct
                                      ? 'bg-[#84cc16]/10 border-[#84cc16]/30 text-[#84cc16]'
                                      : 'bg-black/40 border-white/5 text-white/60'
                                  }`}>
                                    <span className="font-black text-[11px] w-5 flex-shrink-0 opacity-50">{opt.id?.toUpperCase()}.</span>
                                    <span className="flex-1 font-medium">{opt.text}</span>
                                    {opt.is_correct && <Check className="w-4 h-4 flex-shrink-0 text-[#84cc16]" />}
                                  </div>
                                ))}
                              </div>
                              
                              {q.explanation && (
                                <div className="bg-[#00FFFF]/5 border border-[#00FFFF]/20 rounded-lg p-3 text-[11px] text-[#00FFFF]/80 font-medium">
                                  <span className="font-black uppercase tracking-widest text-[#00FFFF] mr-2">Análisis:</span>
                                  {q.explanation}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {isExpanded && !mainTest && (
                      <div className="border-t border-white/5 p-8 bg-black/50 text-center">
                        <AlertCircle className="w-8 h-8 text-white/20 mx-auto mb-3" />
                        <p className="text-[11px] font-black uppercase tracking-widest text-white/40 mb-4">Módulo sin evaluación estructurada</p>
                        <button
                          onClick={() => regenerateSubModuleTest(sm.id)}
                          className="btn-neon-cyan px-6 py-2.5 rounded-lg text-[11px] font-black uppercase tracking-widest"
                        >
                          <Sparkles className="w-4 h-4 inline mr-2" /> Iniciar Generación IA
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* VISTA B: Módulo normal (sin sub-unidades)                          */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {!hasSubModules && sections.length > 0 && (
          <div className="glass-panel p-6">
            
            {usingFallback && (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 flex items-start gap-4 mb-6">
                <AlertCircle className="w-6 h-6 text-orange-500 mt-0.5 flex-shrink-0" />
                <div className="text-[12px] text-orange-200">
                  <p className="font-black uppercase tracking-widest text-orange-400 mb-1">Estructuración Automática Aplicada</p>
                  <p>No se detectaron marcadores de sección <code className="bg-black/40 px-1.5 py-0.5 rounded text-orange-300">## Tema</code> en el texto original. El contenido ha sido fragmentado por la IA. Para mayor precisión, se recomienda estructurar el texto fuente.</p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between px-2 mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#00FFFF]/10 border border-[#00FFFF]/30 rounded-lg text-[#00FFFF]">
                  <BookOpen className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-black tracking-tight text-white">Segmentos de Contenido</h2>
                  {usingFallback && <span className="text-[9px] uppercase tracking-widest font-black text-orange-400">Fragmentación IA</span>}
                </div>
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-white/50 bg-black/40 px-3 py-1.5 rounded-lg border border-white/10">
                {tests.length}/{sections.length} Testeables
              </span>
            </div>

            <div className="space-y-3">
              {sections.map((section, idx) => {
                const sectionTest = tests.find(t =>
                  t.section_id === section.id ||
                  t.section_title?.toLowerCase() === section.title?.toLowerCase()
                );
                const isExpanded = expandedSections[section.id];
                const hasContent = section.content && section.content.length > 50;

                return (
                  <div key={section.id} className="bg-black/30 border border-white/5 rounded-xl overflow-hidden transition-all hover:border-white/10">
                    <div className="flex items-center justify-between hover:bg-white/5 transition-colors">
  <button type="button" onClick={() => toggleSection(section.id)} aria-expanded={!!isExpanded}
    className="flex items-center gap-4 min-w-0 flex-1 text-left p-5 cursor-pointer">
                        <div className={`w-6 h-6 flex items-center justify-center rounded bg-white/5 text-white/40 ${isExpanded ? 'bg-white/10 text-white' : ''}`}>
                          {isExpanded ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                        </div>
                        <div>
                          <span className="text-[10px] font-black text-white/40 uppercase tracking-widest block mb-0.5">
                            {usingFallback ? 'Fragmento' : 'Sección'} {idx + 1}
                          </span>
                          <h3 className="font-bold text-white text-[15px] truncate flex items-center gap-2">
                            {section.title}
                            {!hasContent && <span className="text-[9px] text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded uppercase tracking-widest">Vacío</span>}
                          </h3>
                        </div>
                       </button>
                      
                       <div className="flex items-center gap-4 flex-shrink-0 py-5 pr-5">
                        {sectionTest ? (
                          <div className="flex items-center gap-3">
                            <span className="text-[11px] font-black text-[#ccff00] bg-[#ccff00]/10 border border-[#ccff00]/20 px-2.5 py-1 rounded flex items-center gap-1.5 uppercase tracking-widest">
                              <Check className="w-3 h-3" /> {sectionTest.questions?.length || 0} Qs
                            </span>
                            <button onClick={e => deleteTest(sectionTest.id, e)} className="p-2 text-red-400 hover:text-white hover:bg-red-500/30 rounded-lg transition-colors border border-transparent hover:border-red-500/50">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-white/30 text-[10px] font-black uppercase tracking-widest">
                            <X className="w-3.5 h-3.5" /> Sin Evaluación
                          </div>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-t border-white/5 p-6 bg-black/50 space-y-6">
                        
                        <div>
                          <p className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-3 flex items-center gap-2">
                            Base de Conocimiento <span className="opacity-50">({section.content?.length || 0} chars)</span>
                          </p>
                          <div className="bg-black/60 rounded-xl border border-white/5 p-4 text-[12px] text-white/60 max-h-48 overflow-y-auto font-mono leading-relaxed">
                            {section.content ? section.content.substring(0, 500) + (section.content.length > 500 ? '… [Texto truncado para visualización]' : '') : <span className="italic text-white/20">Segmento sin datos de texto legibles.</span>}
                          </div>
                        </div>

                        {sectionTest?.questions?.length > 0 && (
                          <div>
                            <p className="text-[10px] font-black text-[#00FFFF] uppercase tracking-widest mb-4 flex items-center gap-2">
                              Banco de Preguntas Extraído <span className="opacity-50">({sectionTest.questions.length})</span>
                            </p>
                            <div className="space-y-3">
                              {sectionTest.questions.map((q, qIdx) => (
                                <div key={q.id || qIdx} className="bg-white/5 rounded-xl border border-white/5 p-5">
                                  <div className="flex items-center gap-3 mb-3">
                                    <span className="w-6 h-6 flex items-center justify-center bg-white/10 text-white/50 text-[10px] font-black rounded-md">Q{qIdx + 1}</span>
                                    <span className="px-2.5 py-0.5 bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] text-[10px] font-black uppercase tracking-widest rounded-md">{q.points} PTS</span>
                                  </div>
                                  <p className="font-bold mb-4 text-[14px] text-white leading-snug">{q.question}</p>
                                  
                                  <div className="space-y-2">
                                    {q.options?.map(opt => (
                                      <div key={opt.id} className={`p-3 rounded-lg text-[13px] flex items-center gap-3 border ${
                                        opt.is_correct
                                          ? 'bg-[#84cc16]/10 border-[#84cc16]/30 text-[#84cc16]'
                                          : 'bg-black/40 border-white/5 text-white/60'
                                      }`}>
                                        <span className="font-black text-[11px] w-5 flex-shrink-0 opacity-50">{opt.id?.toUpperCase()}.</span>
                                        <span className="flex-1 font-medium">{opt.text}</span>
                                        {opt.is_correct && <Check className="w-4 h-4 flex-shrink-0 text-[#84cc16]" />}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!hasSubModules && sections.length === 0 && (
          <div className="glass-panel border-orange-500/30 p-12 text-center flex flex-col items-center">
            <AlertCircle className="w-12 h-12 text-orange-500/50 mx-auto mb-4" />
            <p className="font-black text-orange-400 text-lg uppercase tracking-widest mb-2">Base de Conocimiento Vacía</p>
            <p className="text-sm text-white/50 max-w-md">El módulo no contiene suficiente volumen de texto para la generación automatizada de evaluaciones. Requiere actualización de datos estructurados.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModuleTestsManager;